import React, { useContext, useEffect, useState } from 'react'
import './index.scss'
import Button from '@Components/Buttons/Button'
import { StoresContext } from '@App/contexts'
import Navigation from '@App/navigation'
import { RightsholderContact } from '@Entities/rightsholder/rightsholder-contact'
import {
    createRightsholderContactFormFields,
    RightsholderContactFormFields,
} from '@App/forms/rightsholder/rightsholder-contact'
import { FormProvider, useForm } from 'react-hook-form'
import InputTextForm from '@App/kit/fields/InputTextForm'
import SubmitButton from '@App/kit/fields/SubmitButton'
import { useNavigate } from 'react-router'
import RootStore from '@App/stores/root-store'
import ContactNameTooltip from '@Components/RightsholderContact/RightsholderContactInformationEdit/ContactNameTooltip'
import EmailAddressTooltip from '@Components/RightsholderContact/RightsholderContactInformationEdit/EmailAddressTooltip'
import ContactAddressTooltip from './ContactAddressTooltip'
import MailingAddress from '@App/kit/fields/MailingAddress'
import PhoneNumberForm from '@Fields/PhoneNumberForm'
import { observer } from 'mobx-react'
import convertEmptyValuesToUndefined from '@App/forms/v2/helpers/convert-empty-values-to-undefined'
import ContactType from './ContactType'

interface RightsholderContactInformationEditProps {
    createRightsholderContact: boolean
    goBack: () => void
}

const RightsholderContactInformationEdit = (props: RightsholderContactInformationEditProps) => {
    const { preloaderStore, popupStore, contactStore, userStore, rightsholderStore } =
        useContext<RootStore>(StoresContext)
    const navigate = useNavigate()

    const { createRightsholderContact, goBack } = props

    const [contactFormFields] = useState<RightsholderContactFormFields>(createRightsholderContactFormFields())

    const onSave = async (data: RightsholderContact) => {
        preloaderStore.openTransparentPreloader(true)
        data.address = convertEmptyValuesToUndefined(data.address, true)
        data.phoneNumber = convertEmptyValuesToUndefined(data.phoneNumber, true)
        if (createRightsholderContact) {
            try {
                await contactStore.createRightsholderContact(data)
                popupStore.setAlertData('You successfully created a new Contact', false)
                navigate(Navigation.rightholder.maintenance.memberContacts(rightsholderStore.currentRightsholderId))
            } catch (error) {
                popupStore.setAlertFromError(error)
            }
        } else {
            try {
                await contactStore.editRightsholderContact(data)
                popupStore.setAlertData('Your Contact has been successfully updated', false)
                navigate(Navigation.rightholder.maintenance.memberContacts(rightsholderStore.currentRightsholderId))
            } catch (error) {
                popupStore.setAlertFromError(error)
            }
        }
        preloaderStore.openTransparentPreloader(false)
    }

    const methods = useForm<RightsholderContact>({
        mode: 'onChange',
        criteriaMode: 'all',
        reValidateMode: 'onSubmit',
        delayError: 750, // MilliSeconds: this delay is here so email validation doesn't start when user starts typing
        defaultValues: contactStore.rightsholderContact || {
            rightsholderContactTypeId: null,
        },
    })

    useEffect(() => {
        if (!contactStore.rightsholderContacts) {
            contactStore.loadRightsholderContacts().then()
        }
    }, [])

    const title = createRightsholderContact ? 'Add Contact' : 'Edit Contact'

    return (
        <>
            <div className="contact-form-block">
                <div className="body-title">
                    <h3>{title}</h3>
                </div>
                <div className="body-contact-info">
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit(onSave)}>
                            <h5 className="with-tooltip">
                                Contact Name
                                <ContactNameTooltip />
                            </h5>
                            <div className="contact-sub-section">
                                <InputTextForm {...contactFormFields.firstName} />
                                <InputTextForm {...contactFormFields.lastName} />
                            </div>
                            <ContactType rightsholderContactTypeId={contactFormFields.rightsholderContactTypeId} />
                            <div className="with-tooltip">
                                <MailingAddress
                                    title="Contact Address"
                                    toolTip={<ContactAddressTooltip />}
                                    mailingAddressFormProps={contactFormFields.address}
                                    optional
                                />
                            </div>
                            <h5 className="with-tooltip">
                                Contact Information
                                <EmailAddressTooltip />
                            </h5>
                            <div className="contact-sub-section contact-info-sub-section">
                                <PhoneNumberForm phoneNumberFormProps={contactFormFields.phoneNumber} optional />
                                <InputTextForm {...contactFormFields.email} />
                            </div>
                            <div className="buttons-block">
                                <Button mainColor={false} type="primary" submitType="button" onClick={() => goBack()}>
                                    Cancel
                                </Button>
                                <SubmitButton
                                    label="Save"
                                    disabled={userStore.isInternal || !methods.formState.isValid}
                                />
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>
        </>
    )
}

export default observer(RightsholderContactInformationEdit)
